import PropTypes from 'prop-types';
import moment from 'moment';

export const fieldsPropTypes = PropTypes.shape({
    [PropTypes.string.isRequired]: PropTypes.shape({

        /**
         * Id of input field, must correspond to the objects key
         * @example
         * 'field_id'
         */
        id: PropTypes.string.isRequired,

        /**
         * Field type, should be a constant from FormFieldtypes
         * @example
         * FormFieldTypes.STRING
         */
        type: PropTypes.string.isRequired,

        /**
         * Field title
         * @example
         * 'Field title'
         */
        title: PropTypes.string,

        /**
         * Component to override field type (provide custom field), reference to component class
         * @example
         * InputField
         */
        component: PropTypes.elementType,

        /**
         * Options keys depend on the field component implementation
         * @example
         * {
         *     variant: 'outlined'
         * }
         */
        options: PropTypes.object,

        /**
         * Default field value. Is not a placeholder, it will end up in formData
         * Overriding the defaultValue will replace the current formData state
         * @example
         * 'Prefilled name'
         */
        defaultValue: PropTypes.any,

        /**
         * Placeholder text inside field
         * @example
         * 'Enter your name'
         */
        placeholder: PropTypes.any,

        /**
         * Helper text shown below the field. This will only show if the field component implemented this
         * @example
         * 'This field is required'
         */
        helperText: PropTypes.string,

        /**
         * Flag to disable the field
         * @example
         * true
         */
        disabled: PropTypes.bool,

        /**
         * Value options for multi select fields
         *
         * `deselect` value can be used to manipulate the form state
         * when setting options to be disabled. It's possible to have selected disabled options.
         * @example
         * [
         *      {
         *          key: "key",
         *          label: "label",
         *          disabled: false,
         *          deselect: false
         *      }
         * ]
         */
        valueOptions: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            disabled: PropTypes.bool,
            deselect: PropTypes.bool
        })),

        /**
         * UI Schema that is implemented by the field component, to read classes and possible other values
         * @example
         * {
         *     classes: {
         *         root: 'someClassName'
         *     }
         * }
         */
        uiSchema: PropTypes.shape({

            /**
             * Options keys depend on the field component implementation
             * @example
             * {
             *     root: 'className'
             * }
             */
            classes: PropTypes.object
        }),

        /**
         * Validator object for default field validation rules
         * This can be extended by adding a custom validator to the form component
         * Live validation can be enabled with the `liveValidation` form property
         * @example
         * {
         *     required: true,
         *     length: {
         *         min: 1,
         *         max: 10
         *     },
         *     labels: {
         *         required: 'This field is required',
         *         valueMin: 'This field should contain min 1 character',
         *         valueMax: 'This field should contain max 10 characters'
         *     }
         * }
         */
        validator: PropTypes.shape({
            required: PropTypes.bool,
            length: PropTypes.shape({
                min: PropTypes.any,
                max: PropTypes.any
            }),
            value: PropTypes.shape({
                min: PropTypes.any,
                max: PropTypes.any,
                oneOf: PropTypes.array
            }),
            date: PropTypes.shape({
                min: PropTypes.instanceOf(moment),
                max: PropTypes.instanceOf(moment)
            }),
            labels: PropTypes.shape({
                lengthMin: PropTypes.string,
                lengthMax: PropTypes.string,
                dateMin: PropTypes.string,
                dateMax: PropTypes.string,
                valueMin: PropTypes.string,
                valueMax: PropTypes.string,
                oneOf: PropTypes.string,
                required: PropTypes.string
            })
        })
    })
});

export const schemaPropTypes = PropTypes.shape({

    /**
     * Title of the form
     * @example
     * 'form title'
     */
    title: PropTypes.string,

    /**
     * Key value objects corresponding to the Fields proptype
     * @example
     * {
     *     fieldKey: fieldObject
     * }
     */
    fields: fieldsPropTypes
});

export const formDataPropTypes = PropTypes.shape({

    /**
     * Any key value matching the field key
     * @example
     * {
     *     fieldKey: 'field value'
     * }
     */
    [PropTypes.string.isRequired]: PropTypes.any
});

export const errorStatePropTypes = PropTypes.shape({

    /**
     * Error state, based in fields
     * @example
     * {
     *     fieldKey: {
     *         errorText: 'There is something wrong with this field'
     *     }
     * }
     */
    [PropTypes.string]: PropTypes.shape({
        errorText: PropTypes.string
    })
});
